// filepath: apps/web/src/renderers/form/fields/richText/RichText.tsx
//
// FR-UI-CORE-7 AC-2 — RichText field component.
//
// SECURITY (INV-9 binary):
//   This component MUST NEVER use dangerouslySetInnerHTML. The
//   sanitized HTML is parsed with DOMParser and rendered as React
//   elements, one per allowed tag. The static-guard test at
//   apps/web/src/runtime/staticGuards.test.ts INV-9 / AC-3a
//   assertion greps for dangerouslySetInnerHTML anywhere in
//   apps/web/src/ and would block this file from shipping if the
//   anti-pattern were reintroduced.
//
// The rendering algorithm:
//   1. The value is sanitized via DOMPurify.sanitize(value, sanitizeConfig).
//      DOMPurify returns a string that contains only the ALLOWED_TAGS
//      and ALLOWED_ATTR per the sanitizeConfig module. javascript: URIs,
//      inline event handlers, and forbidden tags are stripped.
//   2. The sanitized string is parsed with DOMParser.
//   3. Each child node of the parsed body is converted to a React
//      element via a per-tag switch (textNode / <b> / <a> / ...).
//      Unknown tags fall through to a safe default (text content).
//
// This pattern is intentionally verbose — every allowed tag has an
// explicit React element constructor. A future contributor who adds
// a new tag MUST also add the constructor here AND extend the
// ALLOWED_TAGS list in sanitizeConfig.ts. The two changes are coupled
// and reviewed together.
//
// Note: this is a read-only display component. There is no onChange
// path — rich text editing is a future FR (text editing in the SPA
// is a significantly larger problem; for v1, rich-text values are
// authored server-side and rendered read-only).

import React from "react";
import DOMPurify from "dompurify";

import type { FieldComponentProps } from "../../FieldRegistry";
import { sanitizeConfig } from "./sanitizeConfig";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile } from "@/runtime/useIsMobile";

/**
 * Render sanitized HTML as React elements WITHOUT dangerouslySetInnerHTML.
 *
 * The function is intentionally exported (named) so tests can exercise
 * the per-tag mapping in isolation. It returns React.ReactNode — a
 * fragment containing the rendered tree.
 */
export function renderSanitizedHTML(html: string): React.ReactNode {
  // Sanitize FIRST. DOMPurify.sanitize returns a string that only
  // contains the allowlisted tags/attrs from sanitizeConfig.ts. Any
  // <script>, <iframe>, javascript: URI, onerror=, etc. is stripped.
  const safe = DOMPurify.sanitize(html, sanitizeConfig) as string;

  if (!safe || typeof safe !== "string") {
    return null;
  }

  // Parse the sanitized HTML into a DOM document. The browser's
  // DOMParser is safe here because the input has already been
  // sanitized — at worst the parser will produce <unknown-tag>
  // wrappers which our switch below converts to plain text.
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${safe}</body>`, "text/html");
  const body = doc.body;

  // Convert each child node of the parsed body to a React element.
  // We do NOT use container.innerHTML — every element is built via
  // React.createElement, which React renders as a virtual DOM tree
  // (no innerHTML assignment).
  const children: React.ReactNode[] = [];
  for (const node of Array.from(body.childNodes)) {
    children.push(nodeToReact(node, "rt"));
  }

  return React.createElement(React.Fragment, null, ...children);
}

/**
 * Recursively convert a DOM node into a React element. Text nodes
 * become strings; allowed element nodes become React elements of the
 * matching type; everything else is rendered as text content (safe
 * fallback — DOMPurify has already removed the dangerous bits).
 */
function nodeToReact(node: ChildNode, keyPrefix: string): React.ReactNode {
  // Text node — render as a string. React handles text escaping.
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  // Element node — dispatch by tag name.
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // Collect child content. All children are recursively rendered
    // through nodeToReact so the no-innerHTML invariant is preserved
    // recursively.
    const childNodes: React.ReactNode[] = [];
    for (const child of Array.from(el.childNodes)) {
      childNodes.push(nodeToReact(child, `${keyPrefix}-${tag}`));
    }

    // Per-tag mapping. Every ALLOWED_TAGS entry MUST have a case
    // here. Adding a new tag requires both a sanitizeConfig.ts edit
    // and a switch case below — the two are reviewed together.
    switch (tag) {
      case "b":
      case "strong":
        return React.createElement(
          "strong",
          { key: `${keyPrefix}-${tag}` },
          ...childNodes,
        );
      case "i":
      case "em":
        return React.createElement(
          "em",
          { key: `${keyPrefix}-${tag}` },
          ...childNodes,
        );
      case "u":
        return React.createElement(
          "u",
          { key: `${keyPrefix}-${tag}` },
          ...childNodes,
        );
      case "p":
        return React.createElement(
          "p",
          { key: `${keyPrefix}-p` },
          ...childNodes,
        );
      case "br":
        return React.createElement("br", {
          key: `${keyPrefix}-br-${Math.random()}`,
        });
      case "ul":
        return React.createElement(
          "ul",
          { key: `${keyPrefix}-ul` },
          ...childNodes,
        );
      case "ol":
        return React.createElement(
          "ol",
          { key: `${keyPrefix}-ol` },
          ...childNodes,
        );
      case "li":
        return React.createElement(
          "li",
          { key: `${keyPrefix}-li` },
          ...childNodes,
        );
      case "blockquote":
        return React.createElement(
          "blockquote",
          { key: `${keyPrefix}-blockquote` },
          ...childNodes,
        );
      case "code":
        return React.createElement(
          "code",
          { key: `${keyPrefix}-code` },
          ...childNodes,
        );
      case "pre":
        return React.createElement(
          "pre",
          { key: `${keyPrefix}-pre` },
          ...childNodes,
        );
      case "a": {
        // href passed through DOMPurify has already been validated
        // against ALLOWED_URI_REGEXP — only http(s): / mailto: survive.
        // rel="noopener noreferrer" is mandatory for any external link.
        const href = el.getAttribute("href") ?? undefined;
        const title = el.getAttribute("title") ?? undefined;
        return React.createElement(
          "a",
          {
            key: `${keyPrefix}-a`,
            href,
            title,
            rel: "noopener noreferrer",
            target: "_blank",
          },
          ...childNodes,
        );
      }
      default: {
        // Unknown tag — fall back to text content. DOMPurify should
        // never emit unknown tags (it strips them) but a future
        // ALLOWED_TAGS edit without a corresponding switch case
        // above would land here. Safe default: text only.
        const text = el.textContent ?? "";
        return text;
      }
    }
  }

  // Comment / CDATA / processing instruction / etc. — render nothing.
  // DOMPurify strips comments by default; this is defense-in-depth.
  return null;
}

/**
 * RichText field component (display-only).
 *
 * The component reads `value` (a string) and renders it as sanitized
 * HTML via renderSanitizedHTML. The output is a <div data-testid="...">
 * wrapper so tests can locate the rendered content via standard
 * React Testing Library selectors.
 *
 * FR-UI-CORE-9: the rendered tree carries `role="region"` and
 * `aria-labelledby` pointing at the field's `<label>` id, so screen
 * readers announce the rich-text region by its accessible name.
 * The label id is derived from the fieldId (`field-label-${fieldId}`)
 * — consistent with the rest of the renderer suite.
 */
export function RichText({
  fieldId,
  value,
  uiSchema,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = uiSchema?.fields?.[fieldId];
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const labelId = `field-label-${fieldId}`;
  const helpId = `field-help-${fieldId}`;
  const str = value === undefined || value === null ? "" : String(value);
  const rendered = renderSanitizedHTML(str);

  return (
    <div
      data-testid={`field-${fieldId}-richtext`}
      className="field field-richtext"
      role="region"
      aria-labelledby={labelId}
      style={isMobile ? { maxWidth: "100%" } : {}}
    >
      <label
        id={labelId}
        htmlFor={fieldId}
        style={
          isMobile
            ? { wordBreak: "break-word", overflowWrap: "break-word" }
            : {}
        }
      >
        {labelText}
        {uiField?.required ? <span aria-hidden="true">{" *"}</span> : null}
      </label>
      <div
        className="richtext-content"
        data-testid={`richtext-content-${fieldId}`}
        aria-describedby={helpText ? helpId : undefined}
        style={
          isMobile
            ? {
                wordBreak: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%",
              }
            : {}
        }
      >
        {rendered}
      </div>
      {helpText ? (
        <p
          id={helpId}
          className="field-help"
          style={
            isMobile
              ? { wordBreak: "break-word", overflowWrap: "break-word" }
              : {}
          }
        >
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
